import { Action, createAction, ThunkAction } from '@reduxjs/toolkit';
import { throttle } from 'lodash-es';
import { requestDataIfNecessary } from 'services/api/api.actions';
import { AppState } from 'store/types';
import { selectGetAmountOfMessagesApiConfig } from './inbox.selectors';
import { InboxMessage } from './inbox.types';

/**
 * Triggers a request to remove the message provided as input.
 */
export const requestToDeleteSingleEmail = createAction<
  InboxMessage,
  'inbox/REQUEST_EMAIL_REMOVAL'
>('inbox/REQUEST_EMAIL_REMOVAL');

export const setInboxErrors = createAction<
  Record<string, string>,
  'inbox/SET_INBOX_ERRORS'
>('inbox/SET_INBOX_ERRORS');

/**
 * Dispatched when the user has decided to delete all messages.
 */
export const requestToRemoveAllInboxMessages = createAction(
  'inbox/REQUEST_TO_REMOVE_ALL_MESSAGES',
);

/**
 * Notifies that the one or more transaction that blocked the UI and state
 * updates are completed.
 */
export const inboxUpdateCompleted = createAction<
  'deleteAllMessages' | 'deleteSingleMessage',
  'inbox/UPDATE_COMPLETED'
>('inbox/UPDATE_COMPLETED');

/**
 * Notifies when the user has opened a message or is in a page where the message
 * is featured in a prominent position.
 */
export const userHasReadMessage = createAction<
  { idMessage: number },
  'inbox/USER_IS_READING_MESSAGE'
>('inbox/USER_IS_READING_MESSAGE');

/**
 * Notifies that the user is viewing all the messages.
 * Implies that if there are messages not seen, they should be set as viewed.
 */
export const userHasSeenMessages = createAction(
  'inbox/USER_IS_VIEWING_MESSAGES',
);

/**
 * Used internally (do not export!!!) to throttle the api calls to
 * `/inbox-ms/ms/inbox/getNumberMessage` endpoint.
 */
const requestInboxStatsExecutor = throttle((dispatch, getState) => {
  const apiConfig = selectGetAmountOfMessagesApiConfig(getState());
  dispatch(requestDataIfNecessary(apiConfig));
}, 20_000);

/**
 * Creates a thunk action that invokes an update on the inbox stats,
 * if necessary.
 */
export const requestInboxStatsIfNecessary = (): ThunkAction<
  void,
  AppState,
  void,
  Action
> => {
  return (dispatch, getState) => {
    requestInboxStatsExecutor(dispatch, getState);
  };
};
